import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Heart, Award, Users, Lightbulb, Shield, Scale, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Layout from '../components/layout/Layout';
import { institutionInfo, images, applicationFormUrl, campusGallery } from '../data/mock';

const About = () => {
  const coreValueIcons = {
    Integrity: Shield,
    Excellence: Award,
    Professionalism: Target,
    Innovation: Lightbulb,
    Teamwork: Users,
    Sustainability: Heart,
    'Respect and Equity': Scale,
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${images.students})` }}
        >
          <div className="absolute inset-0 overlay-navy" />
        </div>
        <div className="container-custom relative z-10">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
            About BIT
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Discover our journey, values, and commitment to shaping future leaders.
          </p>
        </div>
      </section>

      {/* Institution Overview */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-6">
                Baobab Institute of Tanzania
              </h2>
              <p className="text-[#475569] leading-relaxed mb-6">
                Located in Mapinga, Bagamoyo District, Tanzania, Baobab Institute of Tanzania (BIT) is a forward-looking higher learning institution dedicated to preparing students for success in an ever-evolving world.
              </p>
              <p className="text-[#475569] leading-relaxed mb-6">
                Our institution offers NACTE-accredited programmes at NTA Levels 4-6 across four departments: Health and Allied Sciences, Business, Hotel and Tourism, and Science and Technology. We combine academic rigor with practical training to ensure our graduates are job-ready from day one.
              </p>
              <p className="text-[#475569] leading-relaxed">
                With a particular focus on securing the digital future, BIT is committed to fostering innovation, entrepreneurship, and social responsibility among our students.
              </p>
            </div>
            <div className="relative">
              <img
                src={images.campus}
                alt="BIT Campus"
                className="w-full h-[400px] object-cover rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#1e3a5f] text-white p-6 rounded-xl shadow-lg">
                <p className="font-display text-4xl font-bold text-[#d4a855]">10+</p>
                <p className="text-sm">Accredited Programmes</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="section-padding bg-[#f8fafc]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#1e3a5f] rounded-2xl flex items-center justify-center">
                <Eye className="w-8 h-8 text-[#d4a855]" />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f]">
                Our Vision
              </h2>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-[#d4a855]">
              <p className="text-[#475569] text-lg leading-relaxed italic">
                "{institutionInfo.vision}"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#1e3a5f] rounded-2xl flex items-center justify-center">
                <Target className="w-8 h-8 text-[#d4a855]" />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f]">
                Our Mission
              </h2>
            </div>
            <div className="bg-[#1e3a5f] p-8 rounded-2xl shadow-lg">
              <p className="text-white/90 text-lg leading-relaxed italic">
                "{institutionInfo.mission}"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="section-padding bg-[#f8fafc]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Our Core Values
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              The principles that guide everything we do at BIT.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {institutionInfo.coreValues.map((value, index) => {
              const Icon = coreValueIcons[value.name] || CheckCircle;
              return (
                <Card key={index} className="bg-white border-none shadow-md card-hover">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#1e3a5f] rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-[#d4a855]" />
                      </div>
                      <div>
                        <h3 className="font-display font-semibold text-[#1e3a5f] text-lg mb-2">
                          {value.name}
                        </h3>
                        <p className="text-[#475569] text-sm leading-relaxed">
                          {value.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-16 bg-[#1e3a5f]">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: '4', label: 'Departments' },
              { number: '10', label: 'Programmes' },
              { number: '2', label: 'Annual Intakes' },
              { number: 'NTA 4-6', label: 'Accreditation Levels' },
            ].map((stat, index) => (
              <div key={index}>
                <p className="font-display text-4xl md:text-5xl font-bold text-[#d4a855] mb-2">
                  {stat.number}
                </p>
                <p className="text-white/80 text-sm md:text-base">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Campus Gallery Section */}
      <section className="section-padding bg-[#f8fafc]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Our Campus
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Explore our modern facilities and vibrant campus life at Baobab Institute of Tanzania.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campusGallery.map((item) => (
              <div 
                key={item.id} 
                className="group relative overflow-hidden rounded-xl shadow-lg aspect-[4/3]"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f]/90 via-[#1e3a5f]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-semibold text-lg">{item.title}</h3>
                    <p className="text-white/80 text-sm">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-custom text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
            Become Part of Our Community
          </h2>
          <p className="text-[#475569] max-w-xl mx-auto mb-8">
            Join BIT and take the first step towards a successful future.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={applicationFormUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#1e3a5f] hover:bg-[#2563eb] text-white font-semibold px-8">
                Apply Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white px-8">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
